import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Eingang } from "../design/Ansicht";
import { Abweisung, Meldung, Status } from "../design/Hinweis";
import { Knopf } from "../design/Kachel";
import { zugang } from "../plattform/api";
import { mitCustomTokenAnmelden, reviereMerken, useSitzung, type GemerktesRevier } from "../plattform/sitzung";
import { OHNE_NETZ } from "./zugangsmeldungen";

/** Feste Wortlaute des Umzugs (oberflaeche.md §9, V-28); ohne Revierbezug, also ohne Ansprache (§2.3). */
export const UMZUG_ANGEMELDET = "Die App ist umgezogen. Nach dem Umziehen die App unter der neuen Adresse zum Home-Bildschirm hinzufügen.";
export const UMZUG_OHNE_ANMELDUNG = "Die App ist umgezogen.";
export const UMZUGSLINK_UNGUELTIG = "Dieser Umzugslink ist nicht mehr gültig. Bitte in der App unter der alten Adresse noch einmal auf „Umziehen“ tippen.";

/**
 * Alte Adresse im Umzugsmodus (K103 (5), V-28): Wer mit einem Konto angemeldet ist, zieht mit „Umziehen“ um — der Dienst
 * stellt einen Umzugslink aus, die App öffnet ihn sofort; er wird nie angezeigt und ist nicht teilbar. Ohne Konto (nicht
 * angemeldet, Link-Zugang) führt „Öffnen“ zur neuen Adresse.
 */
export function Umzugsmodus({ ziel }: { ziel: string }) {
  const { status, art } = useSitzung();
  const [laeuft, setLaeuft] = useState(false);
  const [grund, setGrund] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function umziehen() {
    setLaeuft(true);
    setGrund(null);
    setMeldung(null);
    const a = await zugang("umzug/ausstellen", {}, true);
    const link = a.felder["Umzugslink"];
    if (a.ok && typeof link === "string") {
      window.location.replace(link);
      return;
    }
    setLaeuft(false);
    if (a.netzfehler) setMeldung(OHNE_NETZ);
    else setGrund(a.grund ?? "unzulaessige-angaben");
  }

  const angemeldet = status === "angemeldet" && art === "passkey";
  return (
    <Eingang name="Umzug" titel="Umzug" unter="Neue Adresse der App">
      {status === "laedt" ? <div aria-busy="true" /> : angemeldet ? (
        <Status symbol="info" aktion={<Knopf symbol="passkey" art="planung" onPress={umziehen} isDisabled={laeuft}>Umziehen</Knopf>}>
          {UMZUG_ANGEMELDET}
        </Status>
      ) : (
        <Status symbol="info" aktion={<Knopf art="planung" onPress={() => window.location.assign(`${ziel}/`)}>Öffnen</Knopf>}>
          {UMZUG_OHNE_ANMELDUNG}
        </Status>
      )}
      <Abweisung grund={grund} />
      {meldung ? <Meldung>{meldung}</Meldung> : null}
    </Eingang>
  );
}

/**
 * Neue Adresse, Umzugslink geöffnet (`/umzug#<Token>`, identitaeten.md §11): Das Token verschwindet sofort aus der
 * Adresse, der Dienst löst es genau einmal ein; danach ist die Person angemeldet und die App führt direkt zum Erstellen
 * eines Passkeys (Kontoansicht mit geöffnetem Dialog „Passkey“, oberflaeche.md §7). Ein benutzter oder abgelaufener Link
 * meldet niemanden an.
 */
export function Umzug() {
  const navigate = useNavigate();
  const [meldung, setMeldung] = useState<string | null>(null);
  const begonnen = useRef(false);

  useEffect(() => {
    if (begonnen.current) return;
    begonnen.current = true;
    const token = decodeURIComponent(window.location.hash.replace(/^#/, ""));
    window.history.replaceState(null, "", "/umzug");
    if (!token) {
      setMeldung(UMZUGSLINK_UNGUELTIG);
      return;
    }
    void (async () => {
      const a = await zugang("umzug/einloesen", { Umzugstoken: token });
      const customToken = a.felder["customToken"];
      if (!a.ok || typeof customToken !== "string") {
        setMeldung(a.netzfehler ? OHNE_NETZ : UMZUGSLINK_UNGUELTIG);
        return;
      }
      try {
        const uid = await mitCustomTokenAnmelden(customToken);
        if (Array.isArray(a.felder["Reviere"])) reviereMerken(uid, a.felder["Reviere"] as GemerktesRevier[]);
        navigate("/konto", { replace: true, state: { umzug: true } });
      } catch {
        setMeldung(OHNE_NETZ);
      }
    })();
  }, [navigate]);

  return (
    <Eingang name="Umzug" titel="Umzug" unter="Neue Adresse der App">
      {meldung ? <Meldung>{meldung}</Meldung> : <div aria-busy="true" />}
    </Eingang>
  );
}
